import { useCallback, useEffect, useState } from "react";

import { showSuccessMsg } from "../../../../../utils/Helpers";
import { updateEventCalculationSettings } from "../../../../../network/apis/events";
import { updateCompanyPortalEventCalculationSettings } from "../../../../../network/apis/companyPortalEvents";

/**
 * PROLINE-3796 — "Pay Full Scheduled Shift" (`calculate_by_checkin_only`), daily events only.
 * Read-only in the event response and written only through
 * `PATCH /events/:id/update_calculation_settings` (mirrored in both portals).
 * The response is the updated event; payroll amounts need a list re-fetch.
 */
const useEventPayrollCalculationSettings = ({
  event,
  portal = "admin",
  successMessage = "",
  canEdit = true,
  onSuccess,
} = {}) => {
  const eventId = event?.id;
  const [payFullShift, setPayFullShift] = useState(
    !!event?.calculate_by_checkin_only,
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPayFullShift(!!event?.calculate_by_checkin_only);
  }, [eventId, event?.calculate_by_checkin_only]);

  const handlePayFullShiftChange = useCallback(
    async (value) => {
      if (!eventId || !canEdit) return;

      const updateApi =
        portal === "company"
          ? updateCompanyPortalEventCalculationSettings
          : updateEventCalculationSettings;

      setPayFullShift(value);
      setSaving(true);
      try {
        const response = await updateApi({
          id: eventId,
          params: { calculate_by_checkin_only: value },
        });
        setPayFullShift(
          !!(response?.data?.data?.calculate_by_checkin_only ?? value),
        );
        showSuccessMsg(successMessage || response?.data?.message, true);
        onSuccess?.();
      } catch {
        // Interceptors surface the error message; revert the optimistic toggle.
        setPayFullShift(!value);
      } finally {
        setSaving(false);
      }
    },
    [eventId, portal, successMessage, canEdit, onSuccess],
  );

  return {
    payFullShift,
    saving,
    onPayFullShiftChange: handlePayFullShiftChange,
  };
};

export default useEventPayrollCalculationSettings;
